import { useCallback, useEffect, useRef, useState } from "react"
import RemoteImage from "./RemoteImage"
import { AsOfNote } from "./StaleBanner"
import { useSession } from "../auth/session"
import useCloseRequest from "../hooks/useCloseRequest"
import useOnlineStatus from "../hooks/useOnlineStatus"
import { NA, categoryIcon } from "../models/menu"
import FlagIssue from "./FlagIssue"
import { flagScopeFor } from "../models/snapshot"
import VanSheet from "./VanSheet"
import "./ItemDetail.css"

// Mirrors MenuAndFlashcardViewController: a 550pt hero the content scrolls over,
// a gradient fade into #140D0D, then the flashcard header, Details, the Did You Know
// menu fields (docs/MENU_FIELD_TYPES.md), History and Did You Know sections. PRACTICE
// opens VAN for the item, matching the non-VIP branch of MenuViewController.didTapMenuItem,
// which leaves hidePracticeButton false.
export default function ItemDetail({ item, onClose }) {
  const { restaurant } = useSession()
  const isOnline = useOnlineStatus()
  const [isFlagOpen, setIsFlagOpen] = useState(false)
  const [isVanOpen, setIsVanOpen] = useState(false)

  useCloseRequest(Boolean(item), onClose)

  if (!item) return null

  // hotelName getter: "<ITEM TYPE> - <RESTAURANT NAME>", both uppercased.
  const typeRow = [item.typeLabel, restaurant?.name].filter(Boolean).join(" - ")
  // A translatable field can come back null; never leave the sheet untitled.
  const title = item.name || NA
  // The audio is streamed from S3 and VAN runs over a socket, so both need the network.
  const offlineNote = offlineNoteFor(item)

  return (
    <div className="detail" role="dialog" aria-modal="true" aria-label={title}>
      <div className="detail__sheet">
        {/* Offline, a display image never viewed falls back to the saved thumbnail. */}
        <RemoteImage
          className="detail__hero"
          src={item.displayUrl || item.imageUrl}
          fallbackSrc={[item.imageUrl, item.thumbnailUrl]}
          alt=""
        />

        <button type="button" className="detail__close" aria-label="Close" onClick={onClose}>
          <span className="detail__close-face">
            <img src="/img/close-x.png" alt="" aria-hidden="true" />
          </span>
        </button>

        <div className="detail__scroll scroll-y">
          <div className="detail__hero-spacer" />
          <div className="detail__fade" />

          <div className="detail__panel">
            <img
              className="detail__logo"
              src={`/img/cat/${categoryIcon(item.typeLabel)}.png`}
              alt=""
              aria-hidden="true"
            />

            {typeRow && (
              <div className="detail__type-row">
                <span className="detail__rule" />
                <span className="detail__type">{typeRow}</span>
                <span className="detail__rule" />
              </div>
            )}

            <h2 className="detail__title">{title}</h2>
            {item.headline && <p className="detail__headline">{item.headline}</p>}

            {(item.soundbiteUrl || item.id) && (
              <div className="detail__actions">
                {item.soundbiteUrl && (
                  <Soundbite
                    url={item.soundbiteUrl}
                    isOnline={isOnline}
                    stopWhen={isVanOpen || isFlagOpen}
                  />
                )}
                {item.id && (
                  <button
                    type="button"
                    className="detail__practice"
                    disabled={!isOnline}
                    onClick={() => setIsVanOpen(true)}
                  >
                    Practice
                  </button>
                )}
                {!isOnline && offlineNote && (
                  <p className="detail__offline-note">{offlineNote}</p>
                )}
              </div>
            )}

            <span className="detail__tick" />

            {item.fields.length > 0 && (
              <section className="detail__section">
                <span className="detail__chip">Details</span>
                <div className="detail__fields">
                  {item.fields.map((field, index) => (
                    <div className="detail__field" key={`${field.name}-${index}`}>
                      <span className="detail__field-name">{field.name}</span>
                      {isAllergenField(field) && <AsOfNote className="detail__as-of" />}
                      <p className="detail__field-value">
                        {field.showBullets
                          ? field.values.map((value) => `• ${value}`).join("\n")
                          : field.values.join(", ")}
                      </p>
                      {index < item.fields.length - 1 && <span className="detail__hairline" />}
                    </div>
                  ))}
                </div>
              </section>
            )}

            {(item.cardFields ?? []).map((field, index) => (
              <section className="detail__carousel-section" key={`${field.heading}-${index}`}>
                <SectionHeading title={field.heading || "Did You Know?"} />
                <div className="detail__carousel scroll-x">
                  <div className="detail__carousel-track">
                    {field.cards.map((card, cardIndex) => (
                      <DidYouKnowFieldCard
                        key={`${card.title}-${cardIndex}`}
                        card={card}
                        item={item}
                      />
                    ))}
                  </div>
                </div>
              </section>
            ))}

            {item.itemHistory && (
              <section className="detail__history">
                <img
                  className="detail__logo"
                  src={`/img/cat/${categoryIcon(item.typeLabel)}.png`}
                  alt=""
                  aria-hidden="true"
                />
                <h3 className="detail__history-title">{item.itemHistory.title || "History"}</h3>
                <p className="detail__history-body">{item.itemHistory.description}</p>
              </section>
            )}

            {item.didYouKnow.length > 0 && (
              <section className="detail__carousel-section">
                <SectionHeading title="Did You Know?" />
                <div className="detail__carousel scroll-x">
                  <div className="detail__carousel-track">
                    {item.didYouKnow.map((entry, index) => (
                      <article className="dyk" key={`${entry.title}-${index}`}>
                        <RemoteImage
                          className="dyk__image"
                          src={entry.thumbnailUrl || entry.imageUrl}
                          fallbackSrc={entry.imageUrl}
                          alt=""
                        />
                        <div className="dyk__body">
                          <h4 className="dyk__title">{entry.title || "Did You Know?"}</h4>
                          <p className="dyk__text">{entry.description}</p>
                        </div>
                      </article>
                    ))}
                  </div>
                </div>
              </section>
            )}

            {item.id && (
              <button
                type="button"
                className="flag-trigger t-p14"
                onClick={() => setIsFlagOpen(true)}
              >
                <img src="/img/flag.png" alt="" aria-hidden="true" />
                Flag an Issue
              </button>
            )}
          </div>
        </div>
      </div>

      {isFlagOpen && (
        <FlagIssue itemId={item.id} scope={flagScopeFor(item)} onClose={() => setIsFlagOpen(false)} />
      )}

      {isVanOpen && <VanSheet item={item} onClose={() => setIsVanOpen(false)} />}
    </div>
  )
}

// Names whichever of the two network-only actions this item actually shows.
function offlineNoteFor(item) {
  const blocked = [item.soundbiteUrl && "Soundbite", item.id && "Practice"].filter(Boolean)
  if (!blocked.length) return ""
  return `${blocked.join(" and ")} need${blocked.length > 1 ? "" : "s"} a connection`
}

function isAllergenField(field) {
  return String(field.name).trim().toLowerCase() === "allergens"
}

// A Cards field card: its own image, else the item's, and long text scrolls in the caption.
function DidYouKnowFieldCard({ card, item }) {
  const [src, fallbackSrc] =
    card.thumbnailUrl || card.imageUrl
      ? [card.thumbnailUrl, card.imageUrl]
      : [item.thumbnailUrl, item.imageUrl]
  return (
    <article className="dyk">
      <RemoteImage
        className="dyk__image"
        src={src || fallbackSrc}
        fallbackSrc={fallbackSrc}
        alt=""
      />
      <div className="dyk__body dyk__body--field scroll-y">
        {card.title && <h4 className="dyk__title">{card.title}</h4>}
        {card.description && <p className="dyk__text dyk__text--field">{card.description}</p>}
      </div>
    </article>
  )
}

// Centred Solare title flanked by brand-gold hairlines.
function SectionHeading({ title }) {
  return (
    <div className="detail__heading">
      <span className="detail__heading-rule" />
      <span className="detail__heading-title">{title}</span>
      <span className="detail__heading-rule" />
    </div>
  )
}

function Soundbite({ url, isOnline, stopWhen }) {
  const audioRef = useRef(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [progress, setProgress] = useState(0)

  const stop = useCallback(() => {
    const audio = audioRef.current
    if (!audio) return
    audio.pause()
    audio.currentTime = 0
    setIsPlaying(false)
    setProgress(0)
  }, [])

  useEffect(() => {
    setIsPlaying(false)
    setProgress(0)
  }, [url])

  // A sheet over the item takes the audio with it, rather than leaving it playing underneath.
  useEffect(() => {
    if (stopWhen) stop()
  }, [stopWhen, stop])

  // Closing the item stops playback; an unmounted <audio> can otherwise keep going.
  useEffect(() => () => audioRef.current?.pause(), [])

  const toggle = () => {
    const audio = audioRef.current
    if (!audio) return
    if (audio.paused) {
      audio.play()
      setIsPlaying(true)
    } else {
      audio.pause()
      setIsPlaying(false)
    }
  }

  return (
    <div className="soundbite">
      <audio
        ref={audioRef}
        src={url}
        preload="none"
        onTimeUpdate={(event) => {
          const { currentTime, duration } = event.currentTarget
          setProgress(duration ? currentTime / duration : 0)
        }}
        onEnded={stop}
      />
      {isPlaying ? (
        <div className="soundbite__pill soundbite__pill--playing">
          <button type="button" className="soundbite__icon" aria-label="Pause" onClick={toggle}>
            <PauseIcon />
          </button>
          <span className="soundbite__track">
            <span className="soundbite__fill" style={{ width: `${progress * 100}%` }} />
          </span>
          <button type="button" className="soundbite__icon" aria-label="Stop" onClick={stop}>
            <StopIcon />
          </button>
        </div>
      ) : (
        <button type="button" className="soundbite__pill" onClick={toggle} disabled={!isOnline}>
          Soundbite
        </button>
      )}
    </div>
  )
}

function PauseIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
      <rect x="4" y="3" width="4" height="14" rx="1" />
      <rect x="12" y="3" width="4" height="14" rx="1" />
    </svg>
  )
}

function StopIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
      <rect x="4" y="4" width="12" height="12" rx="1.5" />
    </svg>
  )
}
